import React, { useState, useEffect } from 'react';
import { useSignUp, useAuth } from '@clerk/clerk-react';
import { useHistory, Link, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { API_FORCE_FIREBASE_PASSWORD } from '../../utils/constants';

const SignUpContinuePage = () => {
  const { signUp, setActive, isLoaded: signUpLoaded } = useSignUp();
  const { isSignedIn, isLoaded } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  // Get ticket from URL params
  const searchParams = new URLSearchParams(location.search);
  const ticket = searchParams.get('__clerk_ticket');

  // Redirect if already signed in
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      history.push('/');
    }
  }, [isLoaded, isSignedIn, history]);

  // Initialize sign up with ticket if present
  useEffect(() => {
    if (!signUpLoaded || !ticket) {
      setInitializing(false);
      return;
    }

    const initSignUp = async () => {
      try {
        await signUp.create({
          strategy: 'ticket',
          ticket: ticket
        });
      } catch (err) {
        // If already created or in progress, that's fine
        console.log('Ticket init:', err?.errors?.[0]?.code || 'ok');
      } finally {
        setInitializing(false);
      }
    };

    initSignUp();
  }, [signUpLoaded, ticket, signUp]);

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Check if signUp is loaded
    if (!signUpLoaded || !signUp) {
      setError('Sign up not ready. Please try again.');
      setLoading(false);
      return;
    }

    // Helper function to determine redirect path based on email domain
    const getRedirectPath = (userEmail) => {
      const isAdminDomain =
        userEmail.endsWith('@samxlabs.com') ||
        userEmail.endsWith('@adlerlocal.com') ||
        userEmail.endsWith('@adlersocial.com');
      return isAdminDomain ? '/clients' : '/client';
    };

    try {
      // Invitation flow - only update password
      const result = await signUp.update({ password });

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });

        // Enviar la nueva contraseña al endpoint
        try {
          await fetch(API_FORCE_FIREBASE_PASSWORD, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              email: result.emailAddress,
              password: password
            })
          });
        } catch (passwordSaveError) {
          console.error(
            'Error al enviar contraseña al endpoint:',
            passwordSaveError
          );
          // Continuar con el flujo normal aunque falle
        }

        const redirectPath = getRedirectPath(result.emailAddress || '');
        console.log(
          'Signup complete via invitation, redirecting to:',
          redirectPath
        );
        history.push(redirectPath);
        return;
      }

      if (result.status === 'missing_requirements') {
        const missing = result.missingFields || [];
        if (missing.length > 0) {
          setError(`Please complete: ${missing.join(', ')}`);
        } else {
          setError('Additional information needed');
        }
      }
    } catch (err) {
      console.error('Error during sign up:', err);
      const message =
        err?.errors?.[0]?.longMessage ||
        err?.errors?.[0]?.message ||
        err.message ||
        'Failed to create account';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      {/* Logo */}
      <div className="mb-8">
        <img
          src="../assets/img/adlerlocal_logo_2026.png"
          alt="Adler Local"
          className="h-10"
        />
      </div>

      {/* Sign Up Card */}
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="mb-6 text-center">
            <h1 className="text-xl font-semibold tracking-tight">
              Complete your account
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Create your password below to finish setting up your account.
            </p>
          </div>

          {initializing ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3766a8]"></div>
            </div>
          ) : (
            <form onSubmit={handleSignUp} className="space-y-4">
              <div>
                <Input
                  id="password"
                  type="password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  autoFocus
                />
              </div>

              {error && (
                <p className="text-sm text-destructive text-center">{error}</p>
              )}

              <Button
                type="submit"
                className="w-full !bg-[#3766a8] hover:!bg-[#2d5286] !text-white !border-0"
                disabled={loading || !password}>
                {loading ? 'Creating account...' : 'Complete Registration'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SignUpContinuePage;
